/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { DefaultConnectProps } from '@tock/shared/utils/connect';
import { IWithContextProps } from '@tock/coretypes';
import { Page } from 'components/ui-flow/uiFlowComponent';
import { logger } from '@tock/logging';
import BaseComponent from '@tock/shared/components/BaseComponent';

interface ConnectProps {
  currentPage?: Page;
  nextPage?: Page;
  prevPage?: Page;
}

export default (pageOrder: Page[], isInitialized: (props: any) => boolean = () => true) =>
  <TOriginalProps extends {}>(
    WrappedComponent:
      | React.ComponentClass<TOriginalProps & ConnectProps>
      | React.StatelessComponent<TOriginalProps & ConnectProps>
  ) => {
    type ResultProps = TOriginalProps &
      RouteComponentProps<any> &
      DefaultConnectProps &
      IWithContextProps;
    return class BaseFlowRoute extends BaseComponent<ResultProps, ConnectProps> {
      static displayName = `baseFlow(${WrappedComponent.displayName || WrappedComponent.name})`;

      constructor(props: ResultProps) {
        super(props);
        const currentPage = this.getCurrentPage(props);
        this.state = {
          currentPage,
        };
      }

      UNSAFE_componentWillMount() {
        if (BROWSER) {
          const currentPage = this.getCurrentPage(this.props);
          const isCurrentPageValid = this.isCurrentPageValid(currentPage, this.props);

          if (!isCurrentPageValid) {
            return;
          }

          const pageState = this.computePageState(this.props);
          this.setState(pageState);

          if (!currentPage.shouldShow(this.props)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.props.history.replace(oc(pageState.nextPage).path()!);
          }
        }
      }

      UNSAFE_componentWillReceiveProps(nextProps: ResultProps) {
        const currentPage = this.getCurrentPage(nextProps);
        const isCurrentPageValid = this.isCurrentPageValid(currentPage, nextProps);

        if (!isCurrentPageValid) {
          return;
        }

        const pageState = this.computePageState(nextProps);
        this.setState(pageState);

        if (
          !pageState.currentPage.shouldShow(nextProps) &&
          pageState.nextPage !== pageState.currentPage
        ) {
          this.routeTo(pageState.nextPage);
        }
      }

      routeTo(nextPage: Page) {
        const { actions, history } = this.props;
        actions.checkout.setCheckoutStatus('', '');
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        history.replace(oc(nextPage).path()!);
      }

      isCurrentPageValid(currentPage: any, props: ResultProps): boolean {
        if (!isInitialized(props)) {
          return false;
        }

        if (!currentPage) {
          logger.debug('Could not find a page for path', this.props.location.pathname);
          return false;
        }

        if (currentPage.invalidIf(props)) {
          props.routeTo('/');
          return false;
        }

        return true;
      }

      getCurrentPage(props: ResultProps): any {
        const path = props.location.pathname;
        return _.find(pageOrder, (page) => {
          return _.endsWith(path, page.path);
        });
      }

      computePageState(props: ResultProps) {
        const currentPage = this.getCurrentPage(props);
        const nextPage = this.findNextPage(currentPage, props);
        const prevPage = this.findPrevPage(currentPage, props);
        return {
          currentPage,
          nextPage,
          prevPage,
        };
      }

      findNextPage(currentPage: any, props: ResultProps): Page {
        const currentIndex = _.indexOf(pageOrder, currentPage);
        let nextIndex = currentIndex + 1;
        do {
          if (nextIndex >= pageOrder.length) {
            return pageOrder[pageOrder.length - 1];
          }
          if (!pageOrder[nextIndex].shouldShow(props)) {
            nextIndex++;
            continue;
          }
          return pageOrder[nextIndex];
        } while (nextIndex < pageOrder.length);
        // Could not find a valid page to transition to
        return pageOrder[currentPage];
      }

      findPrevPage(currentPage: any, props: ResultProps): Page {
        const currentIndex = _.indexOf(pageOrder, currentPage);
        let prevIndex = currentIndex - 1;
        do {
          if (prevIndex < 0) {
            return pageOrder[0];
          }
          if (!pageOrder[prevIndex].shouldShow(props)) {
            prevIndex--;
            continue;
          }
          return pageOrder[prevIndex];
        } while (prevIndex >= 0);
        // Could not find a valid page to transition to
        return pageOrder[currentIndex];
      }

      render() {
        if (
          !this.state.currentPage ||
          !isInitialized(this.props) ||
          this.state.currentPage.invalidIf(this.props) ||
          !this.state.currentPage.shouldShow(this.props)
        ) {
          return null;
        }
        // needs fixing due to emotion types working now, see return type of render
        return <WrappedComponent {...(this.props as any)} {...this.state} />;
      }
    };
  };
